/**
 * Event Log Page - View historical events for sessions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { useDiaperStore } from '../stores/diaperStore';
import { loadEvents, loadSessionsFromEvents } from '../lib/api';
import { Event, Session } from '../lib/types';
import { EventCard } from '../components/EventCard';
import { SessionInfoCard } from '../components/SessionInfoCard';
// Note: SessionSelector not used here - using custom select with event counts

// Session from events (simplified structure)
interface EventSession {
  id: number;
  client: string;
  clipName: string;
  clipAddress: string;
  monitored: boolean;
  sessionStartTime: string | null;
  lastEventTime: string;
  eventCount: number;
  siteId: number | null;
  diaperModelName: string | null;
}

export const EventLogPage: React.FC = () => {
  const sites = useAuthStore((state) => state.sites);
  const { activeSessions, currentSite, setCurrentSite, loadSessions } = useSessionsStore();
  const { getDiaperModel } = useDiaperStore();
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [eventLimit, setEventLimit] = useState<number>(500);
  const [totalEventsLoaded, setTotalEventsLoaded] = useState<number>(0);
  const [hasMoreEvents, setHasMoreEvents] = useState<boolean>(false);
  const [sessionsFromEvents, setSessionsFromEvents] = useState<EventSession[]>([]);

  // Load sessions from stored events (includes ended sessions)
  const handleLoadSessionsFromEvents = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const data = await loadSessionsFromEvents();
      setSessionsFromEvents(data.sessions || []);
    } catch (err: any) {
      console.error('Failed to load sessions from events:', err);
      setSessionsFromEvents([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const handleLoadEvents = async () => {
    if (!selectedSessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Use server-side session filtering for efficiency
      const data = await loadEvents({
        limit: eventLimit,
        sessionId: selectedSessionId
      });
      
      // Store pagination info (with defensive checks)
      const eventsArray = data.events || [];
      setTotalEventsLoaded(data.count || eventsArray.length);
      setHasMoreEvents(data.has_more || false);

      setEvents(eventsArray);
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleEventDetails = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Initialize site if not set
  useEffect(() => {
    if (sites && sites.length > 0 && !currentSite) {
      setCurrentSite(sites[0]);
    }
  }, [sites, currentSite, setCurrentSite]);

  // Load sessions once when site changes
  const loadedSiteRef = React.useRef<number | null>(null);
  useEffect(() => {
    if (currentSite && loadedSiteRef.current !== currentSite) {
      loadedSiteRef.current = currentSite;
      loadSessions();
    }
  }, [currentSite, loadSessions]);

  // Sessions from events are loaded manually via button click, not automatically

  // Combine active sessions with sessions from events (if loaded)
  const allSessions: Session[] = React.useMemo(() => {
    const active = activeSessions || [];
    const fromEvents = sessionsFromEvents || [];
    
    // If no ended sessions loaded yet, just return active sessions
    if (fromEvents.length === 0) {
      return active;
    }
    
    // Create a set of active session IDs (convert to string for consistent comparison)
    const activeIds = new Set(active.map(s => String(s.id)));
    
    // Convert event sessions to Session format, excluding those already in active
    // Also filter by current site to only show sessions from the selected site
    // Sort by lastEventTime (most recent first) so most recently ended are at top
    const endedSessions: Session[] = fromEvents
      .filter(es => {
        // Exclude if already in active sessions (compare as strings to avoid type mismatch)
        if (activeIds.has(String(es.id))) return false;
        // Filter by current site (if site is set and session has a siteId)
        if (currentSite && es.siteId && es.siteId !== currentSite) return false;
        return true;
      })
      .sort((a, b) => {
        const timeA = a.lastEventTime || '';
        const timeB = b.lastEventTime || '';
        return timeB.localeCompare(timeA); // Descending order (newest first)
      })
      .map(es => ({
        id: es.id,
        siteId: es.siteId || 0,
        clipAddress: es.clipAddress,
        clipName: es.clipName,
        client: es.client,
        monitored: false, // These are ended sessions
        sessionStartTime: es.sessionStartTime || undefined,
        diaperModelName: es.diaperModelName || undefined,
      }));
    
    // Combine: active sessions first, then ended sessions (sorted by last event time, newest first)
    return [...active, ...endedSessions];
  }, [activeSessions, sessionsFromEvents, currentSite]);

  // Auto-load events when session changes
  useEffect(() => {
    if (selectedSessionId) {
      handleLoadEvents();
    } else {
      setEvents([]);
      setError(null);
      setTotalEventsLoaded(0);
      setHasMoreEvents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId]);

  const selectedSession = allSessions.find((s) => s.id === selectedSessionId);
  const diaperModel = selectedSession?.diaperModelCode ? getDiaperModel(selectedSession.diaperModelCode) : null;
  
  // Find event session info for additional details
  const selectedEventSession = sessionsFromEvents.find(es => es.id === selectedSessionId);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">📜 Session Event Log</h2>
        
        <p className="text-gray-600 mb-4 bg-blue-50 border-l-4 border-primary p-3 rounded">
          📋 View the complete event history for a specific monitoring session. Select a session below to
          load all events that were generated during that care period. Events are displayed in
          chronological order with embedded status images.
        </p>

        {/* Load ended sessions button */}
        <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
          <button
            onClick={handleLoadSessionsFromEvents}
            disabled={isLoadingSessions}
            className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-500 disabled:opacity-60 flex items-center gap-2"
          >
            {isLoadingSessions ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Loading...
              </>
            ) : (
              <>📂 Load Ended Sessions</>
            )}
          </button>
          {sessionsFromEvents.length > 0 && (() => {
            const activeIds = new Set((activeSessions || []).map(s => String(s.id)));
            const endedCount = allSessions.filter(s => !s.monitored).length;
            const totalEndedInOrg = sessionsFromEvents.filter(es => !activeIds.has(String(es.id))).length;
            return (
              <span className="text-sm text-gray-500">
                ✓ Loaded: {endedCount} ended sessions for this site ({totalEndedInOrg} total in org)
              </span>
            );
          })()}
          {sessionsFromEvents.length === 0 && !isLoadingSessions && (
            <span className="text-sm text-gray-400">
              Click to load ended sessions from the last 30 days
            </span>
          )}
        </div>

        <div className="flex gap-3 mb-6 flex-wrap">
          {/* Custom session selector with event count */}
          <select
            value={selectedSessionId || ''}
            onChange={(e) => setSelectedSessionId(e.target.value ? Number(e.target.value) : null)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={isLoading}
          >
            <option value="">Select a session...</option>
            {allSessions.map((session) => {
              const eventSession = sessionsFromEvents.find(es => es.id === session.id);
              const eventCount = eventSession?.eventCount ? ` [${eventSession.eventCount} events]` : '';
              
              if (session.monitored) {
                // Active session - show start date
                const startDate = session.sessionStartTime
                  ? new Date(session.sessionStartTime).toLocaleDateString()
                  : 'Unknown';
                return (
                  <option key={session.id} value={session.id}>
                    🟢 ACTIVE #{session.id} | {session.client} - {session.clipName} (Started: {startDate}){eventCount}
                  </option>
                );
              } else {
                // Ended session - show end time (last event time)
                const endTime = eventSession?.lastEventTime 
                  ? new Date(eventSession.lastEventTime).toLocaleString()
                  : 'Unknown';
                return (
                  <option key={session.id} value={session.id}>
                    ⚪ ENDED #{session.id} | {session.client} - {session.clipName} (Ended: {endTime}){eventCount}
                  </option>
                );
              }
            })}
          </select>
          <div className="flex items-center gap-2">
            <label htmlFor="event-limit" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Max Events:
            </label>
            <input
              id="event-limit"
              type="number"
              min="1"
              max="10000"
              value={eventLimit}
              onChange={(e) => setEventLimit(Math.max(1, parseInt(e.target.value) || 500))}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={isLoading}
            />
          </div>
          <button
            onClick={handleLoadEvents}
            disabled={!selectedSessionId || isLoading}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-60 whitespace-nowrap"
          >
            {isLoading ? '⏳ Loading...' : '📥 Load Events'}
          </button>
        </div>

        {/* Selected Session Info Card */}
        {selectedSession && selectedSession.monitored && (
          <SessionInfoCard
            session={selectedSession}
            diaperModel={diaperModel}
            className="mb-6"
          />
        )}
        
        {/* Info for ended sessions (from events) */}
        {selectedSession && !selectedSession.monitored && selectedEventSession && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-semibold">⚪ Ended Session</span>
              <span className="text-sm text-gray-500">ID: {selectedSession.id}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Client:</span>
                <span className="ml-2 font-medium">{selectedSession.client}</span>
              </div>
              <div>
                <span className="text-gray-500">Clip:</span>
                <span className="ml-2 font-medium">{selectedSession.clipName}</span>
              </div>
              {selectedEventSession.diaperModelName && (
                <div>
                  <span className="text-gray-500">Diaper:</span>
                  <span className="ml-2 font-medium">{selectedEventSession.diaperModelName}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Events:</span>
                <span className="ml-2 font-medium">{selectedEventSession.eventCount}</span>
              </div>
              {selectedEventSession.lastEventTime && (
                <div>
                  <span className="text-gray-500">Last Event:</span>
                  <span className="ml-2 font-medium">
                    {new Date(selectedEventSession.lastEventTime).toLocaleString()}
                  </span>
                </div>
              )}
              {selectedSession.sessionStartTime && (
                <div>
                  <span className="text-gray-500">Started:</span>
                  <span className="ml-2 font-medium">
                    {new Date(selectedSession.sessionStartTime).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {events.length > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            📊 Found {events.length} event(s) for this session
            {totalEventsLoaded > 0 && (
              <span className="ml-2 text-gray-500">
                (loaded {totalEventsLoaded} total events from database)
              </span>
            )}
            {hasMoreEvents && (
              <span className="ml-2 text-amber-600 font-semibold">
                ⚠️ More events available - increase limit to see all
              </span>
            )}
            {events.length >= eventLimit && !hasMoreEvents && (
              <span className="ml-2 text-blue-600">
                (showing all {events.length} events for this session)
              </span>
            )}
          </div>
        )}

        {events.length === 0 && !isLoading && (
          <p className="text-center py-8 text-gray-500">
            {selectedSessionId
              ? '📭 No events found for this session'
              : '👆 Select a session above to view its event history'}
          </p>
        )}

        {events.length > 0 && (
          <div className="space-y-3">
            {events.map((event) => (
              <EventCard
                key={event.timestamp_eventid}
                event={event}
                isExpanded={expandedEvents.has(event.timestamp_eventid)}
                onToggleDetails={toggleEventDetails}
                showNewBadge={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

